import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { AuthForm } from '../components/AuthForm';
import { motion, AnimatePresence, useMotionValue, useSpring } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Plus, Lock, AlertCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { packages, addons, adsOptions, fmtPrice, DEPOSIT_RATE, ADS_MAX, ADS_STEP } from '../data/webServicesData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AnimatedPrice = ({ value, language, className, testId }) => {
  const mv = useMotionValue(value);
  const spring = useSpring(mv, { stiffness: 140, damping: 22 });
  const [display, setDisplay] = useState(value);
  useEffect(() => { mv.set(value); }, [value, mv]);
  useEffect(() => spring.on('change', (v) => setDisplay(Math.round(v * 100) / 100)), [spring]);
  return <span className={className} data-testid={testId}>{fmtPrice(display, language)}</span>;
};

const FunToggle = ({ active, onToggle, testId, label }) => (
  <motion.button
    role="switch"
    aria-checked={active}
    aria-label={label}
    onClick={onToggle}
    whileTap={{ scale: 0.85 }}
    className="relative w-12 h-7 rounded-full shrink-0 transition-colors duration-300"
    style={{ background: active ? 'linear-gradient(135deg, #706958, #e2c179)' : 'rgba(255,255,255,0.12)' }}
    data-testid={testId}
  >
    <motion.span
      className="absolute top-1 left-1 w-5 h-5 rounded-full bg-white flex items-center justify-center"
      animate={{ x: active ? 20 : 0, rotate: active ? 360 : 0 }}
      transition={{ type: 'spring', stiffness: 500, damping: 24 }}
      style={{ boxShadow: active ? '0 0 14px rgba(226,193,121,0.9)' : '0 1px 3px rgba(0,0,0,0.4)' }}
    >
      {active && <Check size={11} strokeWidth={3.5} className="text-black" />}
    </motion.span>
  </motion.button>
);

const AddonRow = ({ addon, active, expanded, onToggle, onExpand, language, t }) => (
  <motion.div
    className="rounded-xl border transition-colors duration-300"
    animate={active ? { scale: [1, 1.015, 1] } : {}}
    transition={{ duration: 0.3 }}
    style={{
      borderColor: active ? 'rgba(226,193,121,0.5)' : 'rgba(255,255,255,0.08)',
      background: active ? 'rgba(226,193,121,0.06)' : 'rgba(255,255,255,0.03)',
      boxShadow: active ? '0 4px 24px rgba(226,193,121,0.1)' : 'none',
    }}
  >
    <div className="flex items-center gap-3 sm:gap-4 p-4 sm:px-5">
      <FunToggle active={active} onToggle={onToggle} testId={`addon-toggle-${addon.id}`} label={addon.name[language]} />
      <button
        onClick={onExpand}
        className="flex-1 flex items-center justify-between gap-3 text-left min-w-0"
        data-testid={`addon-info-${addon.id}`}
        aria-expanded={expanded}
      >
        <span className={`text-sm font-medium leading-snug transition-colors ${active ? 'text-white' : 'text-gray-300'}`}>
          {addon.name[language]}
        </span>
        <span className="flex items-center gap-2.5 shrink-0">
          <span className="text-sm font-semibold" style={{ color: '#e2c179' }}>
            {fmtPrice(addon.price, language)}
            {addon.monthly && <span className="text-xs font-normal text-gray-500">{t('wsPerMonth')}</span>}
          </span>
          <span
            className={`w-6 h-6 rounded-full flex items-center justify-center border border-white/15 text-gray-400 transition-transform duration-300 ${expanded ? 'rotate-45' : ''}`}
          >
            <Plus size={13} />
          </span>
        </span>
      </button>
    </div>
    <AnimatePresence initial={false}>
      {expanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
          className="overflow-hidden"
        >
          <p className="px-5 pb-4 pl-[4.9rem] sm:pl-[5.25rem] text-[13px] leading-relaxed text-gray-500" data-testid={`addon-desc-${addon.id}`}>
            {addon.desc[language]}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

const AdsCard = ({ opt, pkgId, value, onChange, language, t }) => {
  const min = opt.min[pkgId];
  const active = value !== null;
  const pct = active ? ((value - min) / (ADS_MAX - min)) * 100 : 0;
  return (
    <div
      className="rounded-xl border p-5 transition-colors duration-300"
      style={{
        borderColor: active ? 'rgba(226,193,121,0.5)' : 'rgba(255,255,255,0.08)',
        background: active ? 'rgba(226,193,121,0.06)' : 'rgba(255,255,255,0.03)',
      }}
      data-testid={`ads-card-${opt.id}`}
    >
      <div className="flex items-center gap-4">
        <FunToggle
          active={active}
          onToggle={() => onChange(active ? null : min)}
          testId={`ads-toggle-${opt.id}`}
          label={opt.name[language]}
        />
        <img src={`https://cdn.simpleicons.org/${opt.icon}/ffffff`} alt="" className="w-5 h-5 opacity-70" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${active ? 'text-white' : 'text-gray-300'}`}>{opt.name[language]}</p>
          <p className="text-xs text-gray-500 mt-0.5">
            {t('czSuggested')} {packages.find((p) => p.id === pkgId).name[language]}: <span style={{ color: '#e2c179' }}>{fmtPrice(min, language)}{t('wsPerMonth')}</span>
          </p>
        </div>
      </div>
      <AnimatePresence initial={false}>
        {active && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="pt-5">
              <p className="text-[13px] text-gray-500 mb-4">{opt.desc[language]}</p>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-wider text-gray-500">{t('czMonthlyBudget')}</span>
                <span className="text-lg font-bold" style={{ fontFamily: "'Syne', sans-serif", color: '#e2c179' }} data-testid={`ads-value-${opt.id}`}>
                  {fmtPrice(value, language)}<span className="text-xs font-normal text-gray-500">{t('wsPerMonth')}</span>
                </span>
              </div>
              <input
                type="range"
                min={min}
                max={ADS_MAX}
                step={ADS_STEP}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                className="cz-range"
                style={{ background: `linear-gradient(90deg, #e2c179 ${pct}%, rgba(255,255,255,0.12) ${pct}%)` }}
                data-testid={`ads-slider-${opt.id}`}
              />
              <div className="flex justify-between text-[11px] text-gray-600 mt-1.5">
                <span>{fmtPrice(min, language)}</span>
                <span>{fmtPrice(ADS_MAX, language)}</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function Customize() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language, t } = useLanguage();

  const preselect = packages.find((p) => p.id === searchParams.get('pkg'))?.id || null;
  const [phase, setPhase] = useState('intro');
  const [pkgId, setPkgId] = useState(preselect);
  const [selected, setSelected] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [ads, setAds] = useState({ google: null, meta: null });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [payStatus, setPayStatus] = useState('idle');
  const { user } = useAuth();

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.name || '', email: f.email || user.email || '' }));
  }, [user]);
  const videoRef = useRef(null);

  useEffect(() => {
    if (phase !== 'intro') return;
    const timer = setTimeout(() => advanceFromIntro(), 2000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const advanceFromIntro = () => setPhase(preselect ? 'build' : 'package');

  const choosePkg = (id) => {
    setPkgId(id);
    setAds((prev) => {
      const next = { ...prev };
      adsOptions.forEach((opt) => {
        if (next[opt.id] !== null && next[opt.id] < opt.min[id]) next[opt.id] = opt.min[id];
      });
      return next;
    });
    setPhase('build');
  };

  const goBack = () => {
    if (phase === 'build') setPhase('package');
    else navigate('/services');
  };

  const pkg = packages.find((p) => p.id === pkgId);
  const chosen = addons.filter((a) => selected.includes(a.id));
  const oneTime = pkg ? pkg.price + chosen.filter((a) => !a.monthly).reduce((s, a) => s + a.price, 0) : 0;
  const monthlyAddons = chosen.filter((a) => a.monthly).reduce((s, a) => s + a.price, 0);
  const adsMonthly = (ads.google || 0) + (ads.meta || 0);
  const monthly = monthlyAddons + adsMonthly;
  const deposit = Math.round(oneTime * DEPOSIT_RATE * 100) / 100;

  const toggleAddon = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const startCheckout = async (e) => {
    e.preventDefault();
    setPayStatus('sending');
    try {
      const res = await fetch(`${BACKEND_URL}/api/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          package_id: pkgId,
          addon_ids: selected,
          ads: { google: ads.google, meta: ads.meta },
          customer: form,
          language,
          origin_url: window.location.origin,
        }),
      });
      if (!res.ok) throw new Error('checkout failed');
      const data = await res.json();
      if (!data.url) throw new Error('no url');
      window.location.href = data.url;
    } catch {
      setPayStatus('error');
      setTimeout(() => setPayStatus('idle'), 5000);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] overflow-y-auto" style={{ background: '#030303' }} data-testid="customize-page">
      {/* Tiny back arrow */}
      <button
        onClick={goBack}
        className="fixed top-5 left-5 z-[80] w-10 h-10 rounded-full flex items-center justify-center border border-white/20 bg-black/60 backdrop-blur-md text-white transition-all hover:border-white/60 hover:scale-110"
        aria-label="Back"
        data-testid="cz-back-btn"
      >
        <ArrowLeft size={17} />
      </button>

      <AnimatePresence mode="wait">
        {/* PHASE 1: intro video */}
        {phase === 'intro' && (
          <motion.div
            key="intro"
            className="fixed inset-0 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, scale: 1.06 }}
            transition={{ duration: 0.7 }}
            data-testid="cz-intro"
          >
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              onEnded={advanceFromIntro}
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="cz-intro-video"
            >
              <source src="/customize-intro.mp4" type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-black/40" />
            <div className="relative z-10 text-center px-6">
              <motion.p
                className="text-xs md:text-sm font-bold tracking-[0.4em] text-white mb-4"
                style={{ fontFamily: "'Syne', sans-serif" }}
                initial={{ opacity: 0, letterSpacing: '0.2em' }}
                animate={{ opacity: 1, letterSpacing: '0.4em' }}
                transition={{ duration: 1.4, delay: 0.4 }}
              >
                {t('czIntroLine1')}
              </motion.p>
              <motion.p
                className="text-sm text-gray-400"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1.2 }}
              >
                {t('czIntroLine2')}
              </motion.p>
              <motion.div
                className="mx-auto mt-8 h-[2px] w-48 overflow-hidden rounded-full bg-white/20"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                <motion.div
                  className="h-full"
                  style={{ background: 'linear-gradient(90deg, #706958, #e2c179)' }}
                  initial={{ width: '0%' }}
                  animate={{ width: '100%' }}
                  transition={{ duration: 6, ease: 'linear' }}
                />
              </motion.div>
            </div>
            <button
              onClick={advanceFromIntro}
              className="absolute bottom-8 right-8 z-10 px-5 py-2.5 rounded-full text-xs font-semibold tracking-wider uppercase border border-white/25 text-white/80 bg-black/40 backdrop-blur-md transition-all hover:border-white/60 hover:text-white"
              data-testid="cz-skip-btn"
            >
              {t('czSkip')}
            </button>
          </motion.div>
        )}

        {/* PHASE 2: choose package */}
        {phase === 'package' && (
          <motion.div
            key="package"
            className="min-h-full flex flex-col justify-center px-6 lg:px-8 py-24"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.5 }}
            data-testid="cz-package-phase"
          >
            <div className="max-w-[1200px] mx-auto w-full">
              <div className="text-center mb-12">
                <h1
                  className="text-3xl md:text-5xl font-bold tracking-[-0.02em] text-white mb-4"
                  style={{ fontFamily: "'Syne', sans-serif" }}
                  data-testid="cz-package-title"
                >
                  {t('czChoosePackage')}
                </h1>
                <p className="text-gray-500">{t('czChooseSub')}</p>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 max-w-5xl mx-auto">
                {packages.map((p, i) => (
                  <motion.button
                    key={p.id}
                    onClick={() => choosePkg(p.id)}
                    className="group relative h-[300px] sm:h-[380px] md:h-[460px] rounded-2xl overflow-hidden border border-white/10 text-left transition-all duration-300 hover:-translate-y-2"
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + i * 0.1, duration: 0.5 }}
                    whileHover={{ boxShadow: `0 20px 60px ${p.color}35` }}
                    data-testid={`cz-package-${p.id}`}
                  >
                    <img src={p.wallpaper} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70 transition-transform duration-500 group-hover:scale-110" />
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(3,3,3,0.45) 0%, rgba(3,3,3,0.6) 55%, rgba(3,3,3,0.96) 100%)' }} />
                    <div
                      className="absolute top-0 left-0 right-0 h-[3px] opacity-60 group-hover:opacity-100 transition-opacity"
                      style={{ background: `linear-gradient(90deg, transparent, ${p.color}, transparent)` }}
                    />
                    <span
                      className="absolute top-4 left-4 text-xs font-bold tracking-[0.3em] text-white/40 group-hover:text-white/80 transition-colors"
                      style={{ fontFamily: "'Syne', sans-serif" }}
                    >
                      0{i + 1}
                    </span>
                    {p.popular && (
                      <span className="absolute top-4 right-4 px-2.5 py-1 rounded-full text-[9px] font-bold tracking-[0.12em] uppercase text-black" style={{ background: p.color }}>
                        {t('wsMostPopular')}
                      </span>
                    )}
                    <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
                      <h3 className="text-lg md:text-xl font-bold text-white mb-1" style={{ fontFamily: "'Syne', sans-serif" }}>{p.name[language]}</h3>
                      <p className="text-base md:text-lg font-bold" style={{ color: p.color }}>
                        {p.startingAt && <span className="text-xs font-normal text-gray-400 mr-1">{t('wsStartingAt')}</span>}
                        {fmtPrice(p.price, language)}{p.startingAt && '+'}
                      </p>
                      <p className="text-[11px] text-gray-500 mt-1.5 line-clamp-2">{p.tagline[language]}</p>
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* PHASE 3: build */}
        {phase === 'build' && pkg && (
          <motion.div
            key="build"
            className="px-6 lg:px-8 pt-24 pb-24"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            data-testid="cz-build-phase"
          >
            <div className="max-w-[1200px] mx-auto">
              <div className="mb-12">
                <p className="text-xs font-semibold tracking-[0.2em] uppercase mb-2" style={{ color: pkg.color }}>
                  {pkg.name[language]}
                </p>
                <h1
                  className="text-3xl md:text-5xl font-bold tracking-[-0.02em] text-white"
                  style={{ fontFamily: "'Syne', sans-serif" }}
                  data-testid="cz-build-title"
                >
                  {t('czBuildTitle')}
                </h1>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
                <div className="lg:col-span-3 space-y-10">
                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-[0.12em] mb-4" style={{ color: '#e2c179' }}>
                      {t('czAddonsLabel')}
                    </h3>
                    <div className="space-y-2.5">
                      {addons.map((addon) => (
                        <AddonRow
                          key={addon.id}
                          addon={addon}
                          active={selected.includes(addon.id)}
                          expanded={expanded === addon.id}
                          onToggle={() => toggleAddon(addon.id)}
                          onExpand={() => setExpanded(expanded === addon.id ? null : addon.id)}
                          language={language}
                          t={t}
                        />
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-[0.12em] mb-1" style={{ color: '#e2c179' }}>
                      {t('czAdsTitle')}
                    </h3>
                    <p className="text-[13px] text-gray-500 mb-4">{t('czAdsSub')}</p>
                    <div className="space-y-3">
                      {adsOptions.map((opt) => (
                        <AdsCard
                          key={opt.id}
                          opt={opt}
                          pkgId={pkgId}
                          value={ads[opt.id]}
                          onChange={(v) => setAds((prev) => ({ ...prev, [opt.id]: v }))}
                          language={language}
                          t={t}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Summary */}
                <div className="lg:col-span-2">
                  <div className="lg:sticky lg:top-8">
                    <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-md p-7 md:p-8" data-testid="cz-summary">
                      <h3 className="text-lg font-bold text-white mb-6" style={{ fontFamily: "'Syne', sans-serif" }}>
                        {t('wsYourBuild')}
                      </h3>

                      <div className="flex items-center justify-between pb-4 border-b border-white/10">
                        <div>
                          <p className="text-sm font-semibold text-white">{pkg.name[language]}</p>
                          <button
                            onClick={() => setPhase('package')}
                            className="text-[11px] underline underline-offset-2 text-gray-500 hover:text-white transition-colors"
                            data-testid="cz-change-package"
                          >
                            {t('czChangePackage')}
                          </button>
                        </div>
                        <p className="text-sm font-semibold" style={{ color: pkg.color }}>{fmtPrice(pkg.price, language)}</p>
                      </div>

                      <div className="py-4 border-b border-white/10 min-h-[48px]">
                        {chosen.length === 0 && adsMonthly === 0 ? (
                          <p className="text-xs italic text-gray-600" data-testid="cz-no-addons">{t('wsNoAddons')}</p>
                        ) : (
                          <ul className="space-y-2">
                            {chosen.map((a) => (
                              <li key={a.id} className="flex items-center justify-between gap-3" data-testid={`cz-summary-addon-${a.id}`}>
                                <span className="text-[13px] text-gray-400">{a.name[language]}</span>
                                <span className="text-[13px] font-medium text-white shrink-0">
                                  {fmtPrice(a.price, language)}
                                  {a.monthly && <span className="text-[11px] text-gray-500">{t('wsPerMonth')}</span>}
                                </span>
                              </li>
                            ))}
                            {adsOptions.filter((o) => ads[o.id] !== null).map((o) => (
                              <li key={o.id} className="flex items-center justify-between gap-3" data-testid={`cz-summary-ads-${o.id}`}>
                                <span className="text-[13px] text-gray-400">{o.name[language]}</span>
                                <span className="text-[13px] font-medium text-white shrink-0">
                                  {fmtPrice(ads[o.id], language)}<span className="text-[11px] text-gray-500">{t('wsPerMonth')}</span>
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>

                      <div className="pt-5 space-y-2.5">
                        <div className="flex items-end justify-between">
                          <p className="text-sm text-gray-400">{t('wsOneTimeTotal')}</p>
                          <p className="text-3xl font-bold text-white tracking-tight" style={{ fontFamily: "'Syne', sans-serif" }}>
                            <AnimatedPrice value={oneTime} language={language} testId="cz-total" />
                            {pkg.startingAt && <span className="text-lg align-top">+</span>}
                          </p>
                        </div>
                        {monthly > 0 && (
                          <div className="flex items-center justify-between" data-testid="cz-monthly-total">
                            <p className="text-sm text-gray-400">{t('wsMonthlyServices')}</p>
                            <p className="text-base font-semibold" style={{ color: '#e2c179' }}>
                              <AnimatedPrice value={monthly} language={language} />
                              <span className="text-xs font-normal text-gray-500">{t('wsPerMonth')}</span>
                            </p>
                          </div>
                        )}
                      </div>

                      <div
                        className="mt-6 rounded-xl p-4 border"
                        style={{ borderColor: 'rgba(226,193,121,0.35)', background: 'rgba(226,193,121,0.08)' }}
                        data-testid="cz-deposit-box"
                      >
                        <div className="flex items-center justify-between">
                          <p className="text-[13px] font-semibold" style={{ color: '#e2c179' }}>{t('czDeposit')}</p>
                          <p className="text-xl font-bold" style={{ fontFamily: "'Syne', sans-serif", color: '#e2c179' }}>
                            <AnimatedPrice value={deposit} language={language} testId="cz-deposit" />
                          </p>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-[11px] text-gray-500">{t('czRemaining')}</p>
                          <p className="text-[13px] font-medium text-gray-400">{fmtPrice(Math.round((oneTime - deposit) * 100) / 100, language)}</p>
                        </div>
                      </div>

                      <div className="mt-6 space-y-3">
                        {payStatus === 'error' && (
                          <div className="flex items-start gap-2.5 p-3.5 rounded-xl text-sm" style={{ background: 'rgba(239,68,68,0.12)', color: '#fca5a5' }} data-testid="cz-pay-error">
                            <AlertCircle size={17} className="mt-0.5 shrink-0" />
                            <p className="font-medium">{t('czPayError')}</p>
                          </div>
                        )}

                        {!user ? (
                          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5" data-testid="cz-auth-gate">
                            <p className="text-sm font-bold text-white mb-1">{t('czAuthTitle')}</p>
                            <p className="text-xs text-gray-500 mb-5 leading-relaxed">{t('czAuthText')}</p>
                            <AuthForm initialMode="signup" />
                          </div>
                        ) : !showForm ? (
                          <button
                            onClick={() => setShowForm(true)}
                            className="w-full inline-flex items-center justify-center gap-2 rounded-full py-4 text-sm font-bold text-black transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_35px_rgba(226,193,121,0.4)]"
                            style={{ background: 'linear-gradient(135deg, #e2c179, #f5e3b8)' }}
                            data-testid="cz-secure-btn"
                          >
                            <Lock size={14} />
                            {t('czSecurePay')}
                          </button>
                        ) : (
                          <motion.form
                            onSubmit={startCheckout}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="space-y-3"
                            data-testid="cz-checkout-form"
                          >
                            <p className="text-sm font-semibold text-white">{t('czYourInfo')}</p>
                            <input
                              type="text" required value={form.name}
                              onChange={(e) => setForm({ ...form, name: e.target.value })}
                              className="w-full rounded-xl px-4 py-3 text-sm text-white bg-white/[0.06] border border-white/15 outline-none focus:border-[#e2c179] transition-colors placeholder:text-gray-600"
                              placeholder={t('wsQuoteName')}
                              data-testid="cz-name-input"
                            />
                            <input
                              type="email" required value={form.email}
                              onChange={(e) => setForm({ ...form, email: e.target.value })}
                              className="w-full rounded-xl px-4 py-3 text-sm text-white bg-white/[0.06] border border-white/15 outline-none focus:border-[#e2c179] transition-colors placeholder:text-gray-600"
                              placeholder={t('wsQuoteEmail')}
                              data-testid="cz-email-input"
                            />
                            <input
                              type="tel" required value={form.phone}
                              onChange={(e) => setForm({ ...form, phone: e.target.value })}
                              className="w-full rounded-xl px-4 py-3 text-sm text-white bg-white/[0.06] border border-white/15 outline-none focus:border-[#e2c179] transition-colors placeholder:text-gray-600"
                              placeholder={t('wsQuotePhone')}
                              data-testid="cz-phone-input"
                            />
                            <button
                              type="submit"
                              disabled={payStatus === 'sending'}
                              className="w-full inline-flex items-center justify-center gap-2 rounded-full py-4 text-sm font-bold text-black transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                              style={{ background: 'linear-gradient(135deg, #e2c179, #f5e3b8)' }}
                              data-testid="cz-pay-btn"
                            >
                              {payStatus === 'sending' ? t('czRedirecting') : t('czProceedPay')}
                              {payStatus !== 'sending' && <ArrowRight size={15} />}
                            </button>
                          </motion.form>
                        )}
                        <p className="text-[11px] text-gray-600 text-center leading-relaxed flex items-center justify-center gap-1.5">
                          <Lock size={11} className="shrink-0" />
                          {t('czPayNote')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
